import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Archive, RotateCcw, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, apiErrorCode } from "../../lib/api";
import { useChainStore } from "../../data/chainStore";

const isFallen = (deal) =>
  String(deal?.status || "") === "Fallen Through" || String(deal?.stage || "").toLowerCase() === "fallen_through";

/* Archived → Restore; fallen through → Reopen.  The server blocks either
 * when the property already has another active room (active_room_conflict). */
export function RoomLifecycleBanner({ deal }) {
  const { hydrateDeals } = useChainStore();
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  if (!deal) return null;
  const archived = !!deal.archived;
  const fallen = !archived && isFallen(deal);
  if (!archived && !fallen) return null;

  const action = archived ? "restore" : "reopen";
  const run = async () => {
    setBusy(true);
    setConflict(null);
    try {
      await api.post(`/api/deals/${deal.id}/${action}`);
      await hydrateDeals?.();
      toast.success(archived ? "Deal Room restored" : "Transaction reopened");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (apiErrorCode(e) === "active_room_conflict" && detail?.deal_id) {
        setConflict({ message: detail.message, dealId: detail.deal_id });
      } else {
        toast.error(apiError(e, archived ? "Could not restore this room" : "Could not reopen this transaction"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200/80 px-4 py-3 mb-4" data-testid="room-lifecycle-banner" data-state={action}>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-slate-100 text-slate-600 inline-flex items-center justify-center">
            <Archive className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-slate-900" data-testid="room-lifecycle-title">
              {archived ? "This Deal Room is archived" : "This transaction has fallen through"}
            </p>
            <p className="text-[12.5px] text-slate-500">History and the property link are preserved.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-violet-700 text-white text-[13px] font-semibold hover:bg-violet-800 disabled:opacity-60 transition-colors"
          data-testid={`room-lifecycle-${action}-btn`}
        >
          <RotateCcw className="h-4 w-4" />
          {busy ? (archived ? "Restoring…" : "Reopening…") : (archived ? "Restore room" : "Reopen transaction")}
        </button>
      </div>
      {conflict && (
        <div className="mt-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5 flex flex-wrap items-start gap-x-3 gap-y-1.5" data-testid="room-lifecycle-conflict">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
          <p className="text-[12.5px] text-amber-900 flex-1 min-w-[200px]" data-testid="room-lifecycle-conflict-message">{conflict.message}</p>
          <Link
            to={`/deal-rooms/${conflict.dealId}`}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-violet-700 hover:text-violet-800"
            data-testid="room-lifecycle-open-active"
          >
            Open active Deal Room <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
